import React, { useEffect, useState } from 'react';
import { MenuItem, Tab, Tabs, TextField } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { getGroups } from '../../actions/journal';
import AlertsBell from './AlertsBell';
import useIsMobile from '../../utils/useIsMobile';
import {
  JOURNAL_GROUPS, JOURNAL_WORKLOAD, journalGroupLink, journalSheetLink,
} from '../../utils/consts/pathRoutes';

const LAST_GROUP_KEY = 'journal.lastGroupId';

export const rememberGroup = (groupId) => {
  if (groupId) localStorage.setItem(LAST_GROUP_KEY, String(groupId));
};

export const getRememberedGroup = () => localStorage.getItem(LAST_GROUP_KEY);

/**
 * Навигация внутри журнала.
 *
 * Без неё в журнал своей группы можно было попасть только цепочкой
 * «Журнал → список групп → группа → журнал». Преподаватель, который заходит отметить
 * посещаемость, должен попадать туда сразу: последняя открытая группа запоминается,
 * а если её нет — берётся первая доступная.
 */
const JournalNav = ({ activeTab }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isMobile = useIsMobile();

  const [groups, setGroups] = useState([]);
  const [currentGroup, setCurrentGroup] = useState(id || '');

  useEffect(() => {
    getGroups({ status: '' })
      .then((items) => {
        setGroups(items);

        if (id) return;

        const remembered = getRememberedGroup();
        const exists = items.some((group) => String(group.id) === remembered);
        setCurrentGroup(exists ? remembered : (items[0]?.id ? String(items[0].id) : ''));
      })
      .catch(() => setGroups([]));
  }, [id]);

  useEffect(() => {
    if (id) {
      setCurrentGroup(String(id));
      rememberGroup(id);
    }
  }, [id]);

  const isGroupScoped = activeTab === 'group' || activeTab === 'sheet';

  const goToTab = (event, value) => {
    if (value === 'groups') return navigate(JOURNAL_GROUPS);
    if (value === 'workload') return navigate(JOURNAL_WORKLOAD);
    if (!currentGroup) return navigate(JOURNAL_GROUPS);

    return navigate(value === 'sheet'
      ? journalSheetLink(currentGroup)
      : journalGroupLink(currentGroup));
  };

  const changeGroup = (event) => {
    const groupId = event.target.value;
    setCurrentGroup(groupId);
    rememberGroup(groupId);

    // Остаёмся в том же разделе, только меняем группу.
    navigate(location.pathname.endsWith('/sheet')
      ? journalSheetLink(groupId)
      : journalGroupLink(groupId));
  };

  return (
    <div className="journal-nav">
      {/* Четыре вкладки на телефоне в строку не помещаются: там они листаются,
          а стрелки появляются, только когда что-то действительно ушло за край. */}
      <Tabs
        value={activeTab}
        onChange={goToTab}
        TabIndicatorProps={{ sx: { backgroundColor: '#FA7A45', height: 2 } }}
        sx={isMobile
          ? { minHeight: 44, '& .MuiTab-root': { px: 1.5, minWidth: 0 } }
          : { minHeight: 44 }}
        className="journal-nav__tabs"
        {...(isMobile && { variant: 'scrollable', scrollButtons: 'auto', allowScrollButtonsMobile: true })}
      >
        <Tab label="Группы" value="groups" />
        <Tab label="Состав группы" value="group" disabled={!currentGroup} />
        <Tab label="Журнал" value="sheet" disabled={!currentGroup} />
        <Tab label="Выработка" value="workload" />
      </Tabs>

      {/* Выбор группы нужен только на экранах одной группы. На «Группах» и «Выработке»
          он лишний: там и так весь список, а селектор дублирует его и путает. */}
      {isGroupScoped && groups.length > 0 && (
        <TextField
          select label="Группа" value={currentGroup}
          onChange={changeGroup} className="journal-nav__group"
        >
          {groups.map((group) => (
            <MenuItem key={group.id} value={String(group.id)}>
              {group.name} · {group.students_count}/{group.capacity}
            </MenuItem>
          ))}
        </TextField>
      )}

      {/* Колокольчик справа, на всех вкладках: уведомления не привязаны к открытой группе. */}
      <div style={{ marginLeft: 'auto' }}>
        <AlertsBell />
      </div>
    </div>
  );
};

export default JournalNav;
